import * as React from "react";
import {memo, useCallback, useEffect, useState} from "react";
import {Link, NavLink} from "react-router-dom";
import {Dialog, DialogPanel, Menu, MenuButton, MenuItem, MenuItems} from "@headlessui/react";
import {Bars3Icon, XMarkIcon} from "@heroicons/react/24/outline";
import {ChevronDownIcon} from "@heroicons/react/16/solid";
import {Award, Handshake, HeartIcon, Megaphone, Ticket, Trophy, User as UserIcon, Wallet} from "lucide-react";
import {useNavigate} from "react-router";
import {useAuth} from "../context/AuthContext";
// import {useMyPromotions} from "../hooks/useMyPromotions";
import {useMyAgentEarnings} from "../hooks/useMyAgentEarnings";
import Button from "./Button";
import NavDropdown from "./NavDropdown";
import {PERMISSIONS, ROLES} from "../lib/roles";

const SAVED_KEY = "nam_saved_events";

function useSavedCount() {
    const [count, setCount] = useState(() => {
        try {
            const raw = localStorage.getItem(SAVED_KEY);
            return raw ? (JSON.parse(raw) as string[]).length : 0;
        } catch { return 0; }
    });

    useEffect(() => {
        const sync = () => {
            try {
                const raw = localStorage.getItem(SAVED_KEY);
                setCount(raw ? (JSON.parse(raw) as string[]).length : 0);
            } catch { setCount(0); }
        };
        window.addEventListener("storage", sync);
        window.addEventListener("nam-saved-change", sync);
        return () => {
            window.removeEventListener("storage", sync);
            window.removeEventListener("nam-saved-change", sync);
        };
    }, []);

    return count;
}

export type NavItem = {
    label: string;
    to: string;
    className?: string;
    mobileHidden?: boolean;
    target?: string;
};

export type SocialLinks = {
    instagram?: string;
    facebook?: string;
    tiktok?: string;
    x?: string;
};

export type NavDropdownGroup = { type: "dropdown"; label: string; items: NavItem[] };

/** A top-level nav entry: either a direct link or a labeled dropdown of links. */
export type NavNode = NavItem | NavDropdownGroup;

export const isDropdownNode = (node: NavNode): node is NavDropdownGroup =>
    (node as NavDropdownGroup).type === "dropdown";

export type NavLinks = {
    main: NavNode[];
    legal?: NavItem[]; // Footer uses this, but keeping it here avoids duplicate types
    social?: SocialLinks; // Footer renders these as icon links
};

type Props = {
    logo: string;
    invert?: boolean;
    /** When true, navbar starts transparent and solidifies on scroll. Used on the home route. */
    scrollSolidify?: boolean;
    links: NavLinks;
    navClass?: (args: { isActive: boolean }) => string;
    brandName?: string;
};

function useScrolledPast(threshold: number, enabled: boolean) {
    const [past, setPast] = useState(false);
    useEffect(() => {
        if (!enabled) {
            setPast(false);
            return;
        }
        const onScroll = () => setPast(window.scrollY > threshold);
        onScroll();
        window.addEventListener("scroll", onScroll, {passive: true});
        return () => window.removeEventListener("scroll", onScroll);
    }, [threshold, enabled]);
    return past;
}


const cx = (...classes: Array<string | false | null | undefined>) =>
    classes.filter(Boolean).join(" ");

const isExternal = (to: string) => /^https?:\/\//i.test(to);
const isHashLink = (to: string) => to.startsWith("#") || to.startsWith("/#");

const defaultNavClass = ({isActive}: { isActive: boolean }) =>
    cx(
        "text-lg/6 transition",
        isActive ? "text-orange-700 font-bold" : "text-white hover:text-orange-700"
    );

function useRoleFlags(user: any) {
    const roles: string[] = user?.roles ?? [];
    const permissions: string[] = user?.permissions ?? [];
    return {
        isAdmin: roles.includes(ROLES.ADMIN),
        isBusiness: roles.includes(ROLES.BUSINESS),
        // Sales access needs the employee role plus a SALES grant at some business.
        isSales: roles.includes(ROLES.EMPLOYEE) && permissions.includes(PERMISSIONS.SALES),
    };
}

const PrimaryLinks = memo(function PrimaryLinks({
                                                    items,
                                                    navClass,
                                                }: {
    items: NavNode[];
    navClass: (a: { isActive: boolean }) => string;
}) {
    return (
        <>
            {items.map((node) => {
                if (isDropdownNode(node)) {
                    return <NavDropdown key={node.label} label={node.label} items={node.items}/>;
                }

                const item = node;
                const base = cx(item.className, item.mobileHidden ? "hidden md:inline-flex" : "");
                if (isHashLink(item.to)) {
                    return (
                        <a
                            key={item.label}
                            href={item.to}
                            className={cx(navClass({isActive: false}), base)}
                        >
                            {item.label}
                        </a>
                    );
                }
                if (isExternal(item.to)) {
                    return (
                        <a
                            key={item.label}
                            href={item.to}
                            target={item.target ?? "_blank"}
                            rel="noreferrer"
                            className={cx("text-sm font-medium transition text-gray-300 hover:text-white", base)}
                        >
                            {item.label}
                        </a>
                    );
                }

                return (
                    <NavLink
                        to={item.to}
                        key={item.label}
                        className={({isActive}) => cx(navClass({isActive}), base)}
                    >
                        {item.label}
                    </NavLink>
                );
            })}
        </>
    );
});

const UserMenu = memo(function UserMenu({
                                            user,
                                            isAdmin,
                                            isBusiness,
                                            isSales,
                                            hasPromotions,
                                            isSalesAgent,
                                            onLogout,
                                        }: {
    user: { name?: string; email?: string } | null;
    isAdmin: boolean;
    isBusiness: boolean;
    isSales: boolean;
    hasPromotions: boolean;
    isSalesAgent: boolean;
    onLogout: () => void;
}) {
    if (!user) return null;

    return (
        <Menu as="div" className="relative inline-block text-left">
            <MenuButton
                className="inline-flex items-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                aria-label="Open user menu"
            >
                {user.name ?? "Account"}
                <ChevronDownIcon aria-hidden="true" className="-mr-1 size-5 text-gray-400"/>
            </MenuButton>

            <MenuItems
                transition
                className="absolute right-0 z-50 mt-2 w-60 origin-top-right divide-y divide-gray-100 rounded-2xl bg-white p-1.5 shadow-md ring-1 ring-gray-200/70 transition data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75"
            >
                <div className="px-3 py-2.5">
                    <p className="text-xs font-medium uppercase tracking-[0.16em] text-gray-500">Signed in as</p>
                    <p className="truncate text-sm font-medium text-gray-900">{user.email}</p>
                </div>

                <div className="py-1.5">
                    <MenuItem>
                        <Link
                            to="/saved"
                            className="group flex items-center gap-2.5 rounded-xl px-3 py-2 text-sm text-gray-700 data-focus:bg-orange-50 data-focus:text-orange-700"
                        >
                            <HeartIcon className="h-4 w-4 fill-orange-500 stroke-orange-500"/>
                            Saved Events
                        </Link>
                    </MenuItem>

                    <MenuItem>
                        <Link
                            to="/profile"
                            className="group flex items-center gap-2.5 rounded-xl px-3 py-2 text-sm text-gray-700 data-focus:bg-orange-50 data-focus:text-orange-700"
                        >
                            <UserIcon className="h-4 w-4 text-gray-400 group-data-focus:text-orange-700"/>
                            Profile
                        </Link>
                    </MenuItem>

                    <MenuItem>
                        <Link
                            to="/profile/tickets"
                            className="group flex items-center gap-2.5 rounded-xl px-3 py-2 text-sm text-gray-700 data-focus:bg-orange-50 data-focus:text-orange-700"
                        >
                            <Ticket className="h-4 w-4 text-gray-500 group-data-focus:text-orange-700"/>
                            Tickets
                        </Link>
                    </MenuItem>

                    <MenuItem>
                        <Link
                            to="/profile/memberships"
                            className="group flex items-center gap-2.5 rounded-xl px-3 py-2 text-sm text-gray-700 data-focus:bg-orange-50 data-focus:text-orange-700"
                        >
                            <Award className="h-4 w-4 text-gray-500 group-data-focus:text-orange-700"/>
                            Memberships
                        </Link>
                    </MenuItem>

                    <MenuItem>
                        <Link
                            to="/profile/tournaments"
                            className="group flex items-center gap-2.5 rounded-xl px-3 py-2 text-sm text-gray-700 data-focus:bg-orange-50 data-focus:text-orange-700"
                        >
                            <Trophy className="h-4 w-4 text-gray-500 group-data-focus:text-orange-700"/>
                            Tournaments
                        </Link>
                    </MenuItem>

                    {hasPromotions && (
                        <MenuItem>
                            <Link
                                to="/profile/promotions"
                                className="group flex items-center gap-2.5 rounded-xl px-3 py-2 text-sm text-gray-700 data-focus:bg-orange-50 data-focus:text-orange-700"
                            >
                                <Megaphone className="h-4 w-4 text-gray-500 group-data-focus:text-orange-700"/>
                                My Promotions
                            </Link>
                        </MenuItem>
                    )}

                    {isSalesAgent && (
                        <MenuItem>
                            <Link
                                to="/profile/sales-agent"
                                className="group flex items-center gap-2.5 rounded-xl px-3 py-2 text-sm text-gray-700 data-focus:bg-orange-50 data-focus:text-orange-700"
                            >
                                <Handshake className="h-4 w-4 text-gray-500 group-data-focus:text-orange-700"/>
                                Sales Agent
                            </Link>
                        </MenuItem>
                    )}

                    <MenuItem>
                        <Link
                            to="/profile/account"
                            className="group flex items-center gap-2.5 rounded-xl px-3 py-2 text-sm text-gray-700 data-focus:bg-orange-50 data-focus:text-orange-700"
                        >
                            <Wallet className="h-4 w-4 text-gray-500 group-data-focus:text-orange-700"/>
                            Cashless Account
                        </Link>
                    </MenuItem>
                </div>

                {(isBusiness || isAdmin || isSales) && (
                    <div className="py-1.5">
                        <MenuItem>
                            <Link
                                to="/business/dashboard"
                                className="block rounded-xl px-3 py-2 text-sm text-gray-700 data-focus:bg-orange-50 data-focus:text-orange-700"
                            >
                                Business Hub
                            </Link>
                        </MenuItem>
                        {isAdmin && (
                            <MenuItem>
                                <Link
                                    to="/admin/dashboard"
                                    className="block rounded-xl px-3 py-2 text-sm text-gray-700 data-focus:bg-orange-50 data-focus:text-orange-700"
                                >
                                    Admin Hub
                                </Link>
                            </MenuItem>
                        )}
                    </div>
                )}

                <div className="py-1.5">
                    <MenuItem>
                        <button
                            type="button"
                            onClick={onLogout}
                            className="block w-full rounded-xl px-3 py-2 text-left text-sm text-gray-700 data-focus:bg-orange-50 data-focus:text-orange-700"
                        >
                            Sign out
                        </button>
                    </MenuItem>
                </div>
            </MenuItems>
        </Menu>
    );
});

const AuthButtons = memo(function AuthButtons() {
    return (
        <div className="flex gap-2">
            <Button variant="outline" size="md" to="/login">
                Log In
            </Button>
            <Button variant="primary" size="md" to="/signup">
                Sign Up
            </Button>
        </div>
    );
});

export default function Navbar({
                                   logo,
                                   invert,
                                   scrollSolidify = false,
                                   links,
                                   navClass = defaultNavClass,
                                   brandName = "NamEvents",
                               }: Props) {
    const {user, logout} = useAuth();
    const {isAdmin, isBusiness, isSales} = useRoleFlags(user);

    // Off again: right after login this request ends the session (cause not yet found).
    // The page itself stays reachable at /profile/promotions.
    // const {data: promotions} = useMyPromotions({enabled: !!user});
    // const hasPromotions = (promotions?.length ?? 0) > 0;
    const hasPromotions = false;
    // const {data: agentEarnings} = useMyAgentEarnings({enabled: !!user});
    // const isSalesAgent = !!agentEarnings;

    const navigate = useNavigate();
    const [mobileOpen, setMobileOpen] = useState(false);
    const scrolled = useScrolledPast(32, scrollSolidify);

    const closeMobile = useCallback(() => setMobileOpen(false), []);
    const openMobile = useCallback(() => setMobileOpen(true), []);

    const handleLogout = useCallback(() => {
        logout();
        navigate("/", {replace: true});
    }, [logout, navigate]);

    const savedCount = useSavedCount();

    const solid = invert || (scrollSolidify && scrolled);

    return (
        <header
            className={cx(
                scrollSolidify ? "fixed" : "absolute",
                "inset-x-0 top-0 z-50 transition-colors duration-300",
                solid && "bg-zinc-950/85 backdrop-blur ring-1 ring-white/5"
            )}
        >
            <nav
                aria-label="Global"
                className="flex items-center justify-between p-4 lg:px-8"
            >
                {/* Brand */}
                <div className="flex lg:flex-1">
                    <Link to="/" className="-m-1.5 p-1.5" aria-label={brandName} onClick={closeMobile}>
                        <img
                            src={logo}
                            alt={brandName}
                            className="h-16 w-auto invert"
                            loading="eager"
                            decoding="async"
                        />
                        <span className="sr-only">{brandName}</span>
                    </Link>
                </div>

                {/* Desktop nav */}
                <div className="hidden lg:flex lg:items-center lg:gap-x-6">
                    <PrimaryLinks items={links.main} navClass={navClass}/>
                </div>

                {/* Desktop right side */}
                <div className="hidden lg:flex lg:flex-1 lg:items-center lg:justify-end lg:gap-4">
                    <Link
                        to="/saved"
                        aria-label="Saved events"
                        className="relative rounded-full p-2 text-white/70 hover:text-white transition-colors"
                    >
                        <HeartIcon className={`h-5 w-5 transition-all ${savedCount > 0 ? "fill-orange-500 stroke-orange-500" : "fill-transparent"}`}/>
                        {savedCount > 0 && (
                            <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-orange-700 text-[10px] font-bold text-white">
                                {savedCount > 9 ? "9+" : savedCount}
                            </span>
                        )}
                    </Link>
                    {!user ? (
                        <AuthButtons/>
                    ) : (
                        <UserMenu user={user} isAdmin={isAdmin} isBusiness={isBusiness} isSales={isSales} hasPromotions={hasPromotions} isSalesAgent={false} onLogout={handleLogout}/>
                    )}
                </div>

                {/* Mobile trigger */}
                <div className="flex lg:hidden">
                    <button
                        type="button"
                        onClick={openMobile}
                        className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-200 hover:text-white hover:bg-white/10"
                        aria-label="Open main menu"
                    >
                        <Bars3Icon aria-hidden="true" className="size-6"/>
                    </button>
                </div>
            </nav>

            {/* Mobile menu */}
            <Dialog open={mobileOpen} onClose={setMobileOpen} className="lg:hidden">
                <div className="fixed inset-0 z-50"/>
                <DialogPanel
                    className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-gray-800/95 p-4 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
                    <div className="flex items-center justify-between">
                        <Link to="/" className="-m-1.5 p-1.5" aria-label={brandName} onClick={closeMobile}>
                            <img src={logo} alt={brandName} className="h-16 w-auto invert"/>
                        </Link>
                        <button
                            type="button"
                            onClick={closeMobile}
                            className="-m-2.5 rounded-md p-2.5 text-gray-300 hover:text-white hover:bg-white/10"
                            aria-label="Close menu"
                        >
                            <XMarkIcon aria-hidden="true" className="size-6"/>
                        </button>
                    </div>

                    <div className="mt-6 space-y-6">
                        {/* Links */}
                        <nav className="space-y-2">
                            {links.main.map((node) => {
                                if (isDropdownNode(node)) {
                                    return (
                                        <div key={node.label} className="pt-4">
                                            <p className="px-3 pb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">
                                                {node.label}
                                            </p>
                                            <div className="space-y-2">
                                                {node.items.map((item) => {
                                                    if (isExternal(item.to)) {
                                                        return (
                                                            <a
                                                                key={item.to}
                                                                href={item.to}
                                                                target={item.target ?? "_blank"}
                                                                rel="noreferrer"
                                                                onClick={closeMobile}
                                                                className="block rounded-md px-3 py-2 text-base font-medium text-gray-300 hover:bg-white/5 hover:text-white"
                                                            >
                                                                {item.label}
                                                            </a>
                                                        );
                                                    }

                                                    return (
                                                        <NavLink
                                                            key={item.to}
                                                            to={item.to}
                                                            onClick={closeMobile}
                                                            className={({isActive}) =>
                                                                cx(
                                                                    "block rounded-md px-3 py-2 text-base font-medium",
                                                                    isActive ? "bg-white/10 text-white" : "text-gray-300 hover:bg-white/5 hover:text-white"
                                                                )
                                                            }
                                                        >
                                                            {item.label}
                                                        </NavLink>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                }

                                const item = node;
                                if (item.mobileHidden) return null;

                                if (isHashLink(item.to)) {
                                    return (
                                        <a
                                            key={item.to}
                                            href={item.to}
                                            onClick={closeMobile}
                                            className={cx(
                                                "block rounded-md px-3 py-2 text-base font-medium",
                                                "text-gray-300 hover:bg-white/5 hover:text-white",
                                                item.className
                                            )}
                                        >
                                            {item.label}
                                        </a>
                                    );
                                }

                                if (isExternal(item.to)) {
                                    return (
                                        <a
                                            key={item.to}
                                            href={item.to}
                                            target={item.target ?? "_blank"}
                                            rel="noreferrer"
                                            onClick={closeMobile}
                                            className={cx(
                                                "block rounded-md px-3 py-2 text-base font-medium",
                                                "text-gray-300 hover:bg-white/5 hover:text-white",
                                                item.className
                                            )}
                                        >
                                            {item.label}
                                        </a>
                                    );
                                }

                                return (
                                    <NavLink
                                        key={item.to}
                                        to={item.to}
                                        onClick={closeMobile}
                                        className={({isActive}) =>
                                            cx(
                                                "block rounded-md px-3 py-2 text-base font-medium",
                                                isActive ? "bg-white/10 text-white" : "text-gray-300 hover:bg-white/5 hover:text-white",
                                                item.className
                                            )
                                        }
                                    >
                                        {item.label}
                                    </NavLink>
                                );
                            })}
                        </nav>

                        {/* Auth / User */}
                        {!user ? (
                            <div className="flex gap-2">
                                <Button variant="outline" size="md" to="/login" onClick={closeMobile}>
                                    Log In
                                </Button>
                                <Button variant="primary" size="md" to="/signup" onClick={closeMobile}>
                                    Sign Up
                                </Button>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                <div className="rounded-lg bg-white/5 p-4">
                                    <p className="text-sm text-gray-300">Signed in as</p>
                                    <p className="truncate text-sm font-medium text-white">{user.email}</p>
                                </div>

                                <Link
                                    to="/profile"
                                    onClick={closeMobile}
                                    className="block rounded-md px-3 py-2 text-base text-gray-300 hover:bg-white/5 hover:text-white"
                                >
                                    Profile & Tickets
                                </Link>

                                <Link
                                    to="/saved"
                                    onClick={closeMobile}
                                    className="flex items-center gap-2 rounded-md px-3 py-2 text-base text-gray-300 hover:bg-white/5 hover:text-white"
                                >
                                    <HeartIcon className="h-4 w-4 fill-orange-500 stroke-orange-500"/>
                                    Saved Events
                                    {savedCount > 0 && (
                                        <span className="ml-auto flex h-5 w-5 items-center justify-center rounded-full bg-orange-700 text-[11px] font-bold text-white">
                                            {savedCount > 9 ? "9+" : savedCount}
                                        </span>
                                    )}
                                </Link>

                                <Link
                                    to="/profile/memberships"
                                    onClick={closeMobile}
                                    className="block rounded-md px-3 py-2 text-base text-gray-300 hover:bg-white/5 hover:text-white"
                                >
                                    Memberships
                                </Link>

                                <Link
                                    to="/profile/tournaments"
                                    onClick={closeMobile}
                                    className="block rounded-md px-3 py-2 text-base text-gray-300 hover:bg-white/5 hover:text-white"
                                >
                                    Tournaments
                                </Link>

                                {hasPromotions && (
                                    <Link
                                        to="/profile/promotions"
                                        onClick={closeMobile}
                                        className="block rounded-md px-3 py-2 text-base text-gray-300 hover:bg-white/5 hover:text-white"
                                    >
                                        My Promotions
                                    </Link>
                                )}

                                {/*{isSalesAgent && (*/}
                                {/*    <Link*/}
                                {/*        to="/profile/sales-agent"*/}
                                {/*        onClick={closeMobile}*/}
                                {/*        className="block rounded-md px-3 py-2 text-base text-gray-300 hover:bg-white/5 hover:text-white"*/}
                                {/*    >*/}
                                {/*        Sales Agent*/}
                                {/*    </Link>*/}
                                {/*)}*/}

                                <Link
                                    to="/profile/account"
                                    onClick={closeMobile}
                                    className="block rounded-md px-3 py-2 text-base text-gray-300 hover:bg-white/5 hover:text-white"
                                >
                                    Account
                                </Link>

                                {(isBusiness || isAdmin || isSales) && (
                                    <Link
                                        to="/business/dashboard"
                                        onClick={closeMobile}
                                        className="block rounded-md px-3 py-2 text-base text-gray-300 hover:bg-white/5 hover:text-white"
                                    >
                                        Business Hub
                                    </Link>
                                )}

                                {(isAdmin) && (
                                    <Link
                                        to="/admin/dashboard"
                                        onClick={closeMobile}
                                        className="block rounded-md px-3 py-2 text-base text-gray-300 hover:bg-white/5 hover:text-white"
                                    >
                                        Admin Hub
                                    </Link>
                                )}

                                <button
                                    type="button"
                                    onClick={() => {
                                        closeMobile();
                                        handleLogout();
                                    }}
                                    className="mt-2 block w-full rounded-md px-3 py-2 text-left text-base text-red-300 hover:bg-red-400/10 hover:text-red-200"
                                >
                                    Sign out
                                </button>
                            </div>
                        )}
                    </div>
                </DialogPanel>
            </Dialog>
        </header>
    );
}